
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {ChartInfoListResponse} from './ChartInfoListResponse';

/**
 * ChartInfoListRequest struct
 */
class ChartInfoListRequest extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {!UUID=} dashboardId
   * @param {!boolean=} getFullInfo
   * @constructor
   */
  constructor (parent = new Request(), dashboardId = new UUID(), getFullInfo = false) {
    super()
    super.copy(parent)
    this.DashboardId = dashboardId
    this.GetFullInfo = getFullInfo
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ChartInfoListRequest}
   * @param {!ChartInfoListRequest} other Other struct
   * @returns {!ChartInfoListRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.DashboardId != null) {
      this.DashboardId = new UUID(other.DashboardId)
    } else {
      this.DashboardId = null
    }
    if (other.GetFullInfo != null) {
      this.GetFullInfo = other.GetFullInfo
    } else {
      this.GetFullInfo = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ChartInfoListRequest}
   * @returns {!ChartInfoListRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ChartInfoListRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ChartInfoListRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ChartInfoListRequest}
   * @param {!ChartInfoListRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ChartInfoListRequest)) {
      throw new TypeError('Instance of ChartInfoListRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ChartInfoListRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      DashboardId: ((this.DashboardId != null) ? this.DashboardId.toString() : null),
      GetFullInfo: ((this.GetFullInfo != null) ? this.GetFullInfo : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ChartInfoListRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ChartInfoListRequest} other Object value
   * @returns {!ChartInfoListRequest} Created struct
   */
  static fromObject (other) {
    return new ChartInfoListRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ChartInfoListRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ChartInfoListRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ChartInfoListRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5195
  }
}

export { ChartInfoListRequest };

/**
 * Fast Binary Encoding ChartInfoListRequest field model
 */
class FieldModelChartInfoListRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.DashboardId = new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.GetFullInfo = new fbe.FieldModelBool(buffer, this.DashboardId.fbeOffset + this.DashboardId.fbeSize)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelChartInfoListRequest}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelChartInfoListRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelChartInfoListRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.DashboardId.fbeSize + this.GetFullInfo.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelChartInfoListRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.DashboardId.fbeExtra + this.GetFullInfo.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelChartInfoListRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelChartInfoListRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelChartInfoListRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5195
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelChartInfoListRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelChartInfoListRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.DashboardId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DashboardId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DashboardId.fbeSize

    if ((fbeCurrentSize + this.GetFullInfo.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.GetFullInfo.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.GetFullInfo.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelChartInfoListRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelChartInfoListRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelChartInfoListRequest}
   * @param {!ChartInfoListRequest} fbeValue Default value, defaults is new ChartInfoListRequest()
   * @returns {!ChartInfoListRequest} ChartInfoListRequest value
   */
  get (fbeValue = new ChartInfoListRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelChartInfoListRequest}
   * @param {!ChartInfoListRequest} fbeValue ChartInfoListRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.DashboardId.fbeSize) <= fbeStructSize) {
      fbeValue.DashboardId = this.DashboardId.get()
    } else {
      fbeValue.DashboardId = new UUID()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DashboardId.fbeSize

    if ((fbeCurrentSize + this.GetFullInfo.fbeSize) <= fbeStructSize) {
      fbeValue.GetFullInfo = this.GetFullInfo.get()
    } else {
      fbeValue.GetFullInfo = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.GetFullInfo.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelChartInfoListRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelChartInfoListRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelChartInfoListRequest}
   * @param {!ChartInfoListRequest} fbeValue ChartInfoListRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelChartInfoListRequest}
   * @param {ChartInfoListRequest} fbeValue ChartInfoListRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.DashboardId.set(fbeValue.DashboardId)
    this.GetFullInfo.set(fbeValue.GetFullInfo)
  }
}

export { FieldModelChartInfoListRequest };

/**
 * Fast Binary Encoding ChartInfoListRequest model
 */
class ChartInfoListRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelChartInfoListRequest(this.buffer, 4)
  }

  /**
   * Get the ChartInfoListRequest model
   * @this {!ChartInfoListRequestModel}
   * @returns {!FieldModelChartInfoListRequest} model ChartInfoListRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ChartInfoListRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ChartInfoListRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ChartInfoListRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ChartInfoListRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelChartInfoListRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ChartInfoListRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ChartInfoListRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ChartInfoListRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ChartInfoListRequestModel}
   * @param {!ChartInfoListRequest} value ChartInfoListRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ChartInfoListRequestModel}
   * @param {!ChartInfoListRequest} value ChartInfoListRequest value, defaults is new ChartInfoListRequest()
   * @return {!object} Deserialized ChartInfoListRequest value and its size
   */
  deserialize (value = new ChartInfoListRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ChartInfoListRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ChartInfoListRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ChartInfoListRequestModel}
   * @param {!number} prev Previous ChartInfoListRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ChartInfoListRequestModel };
ChartInfoListRequest.__has_response = true;
ChartInfoListRequest.__response_class = ChartInfoListResponse;
